import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Pagination,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Badge,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ReplyIcon from "@mui/icons-material/Reply";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";

import {
  useLazyWhatsdetailappReportQuery,
  useGetWhatsapprepliesQuery,
} from "../store/services/whatsappApi";
import { cellSX } from "../component/common/commonStyle";

/* ---------- helpers ---------- */
const getStatusColor = (status) => {
  switch (status) {
    case "delivered": return "success";
    case "read": return "primary";
    case "sent": return "info";
    case "failed": return "error";
    default: return "default";
  }
};

const maskPhone = (num) => {
  const str = String(num || "");
  return str.length < 7 ? str : str.slice(0, 3) + "****" + str.slice(-3);
};

const formatTimestamp = (timestamp) =>
  timestamp ? dayjs(timestamp).format("DD/MM/YYYY HH:mm:ss") : "-";

const formatTimeDuration = (ms) => {
  if (!ms) return "-";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const normalizePhoneNumber = (phone) => {
  if (phone == null) return "";
  if (typeof phone === "string" || typeof phone === "number") return String(phone);
  if (Array.isArray(phone)) return phone.map(normalizePhoneNumber).filter(Boolean).join(", ");
  if (typeof phone === "object") return phone.phone || phone.number || phone.id || phone.msisdn || phone.to || JSON.stringify(phone);
  return String(phone);
};

const DEFAULT_LIMIT = 25;
const DEFAULT_STATUS = "all";

/* ---------- Component ---------- */
const WhatsappDetailPage = () => {
  const { campaign_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.openReplies ? 1 : 0);

  const [customerId, setCustomerId] = useState(null);

  // Replies API — response shape is:
  // { replies: { "<phone_number>": { inbound: [...], outbound: [...] } } }
  // where each entry has { message_id, type, text, status, has_media, timestamp, template_name }
  const { data: whatsappRepliesResponse, isFetching: repliesLoading } =
    useGetWhatsapprepliesQuery(
      { campaign_id, customer_id: customerId },
      { skip: !campaign_id || !customerId },
    );

  const [fetchCampaignReport, { isFetching: messagesLoading }] =
    useLazyWhatsdetailappReportQuery();

  // Current page data (from the server, for the current offset/status)
  const [campaign, setCampaign] = useState({});
  const [messages, setMessages] = useState([]);
  const [summary, setSummary] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);

  // Offset-based pagination for the Phone Numbers tab
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT);

  // Filters
  const [phoneSearch, setPhoneSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS);
  const [replySearch, setReplySearch] = useState("");

  // Replies (button type only), flattened from the replies API's per-number inbound/outbound shape
  const [allReplies, setAllReplies] = useState([]);

  // Reply pagination (client-side, over allReplies)
  const [replyPage, setReplyPage] = useState(1);
  const [replyRowsPerPage, setReplyRowsPerPage] = useState(10);

  // Decode customer id from token
  useEffect(() => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) return toast.error("Access token not found");
      const decoded = jwtDecode(token);
      const id = decoded?.customer_id || decoded?.id || decoded?.userid;
      if (!id) toast.error("Customer ID not found in token");
      else setCustomerId(id);
    } catch {
      toast.error("Invalid access token format");
    }
  }, []);

  // Flatten { "<phone>": { inbound: [...], outbound: [...] } } into a single
  // array of inbound (customer) replies for the Replies tab.
  // Only replies whose type is "button" are kept; outbound entries are skipped.
  useEffect(() => {
    const repliesByNumber = whatsappRepliesResponse?.replies || {};

    const replies = Object.entries(repliesByNumber).flatMap(([phoneNumber, directions]) => {
      const buttonMsgs = (directions?.inbound || []).filter(
        (msg) => String(msg.type || "").toLowerCase() === "button",
      );

      return buttonMsgs.map((msg, idx) => ({
        phone_number: normalizePhoneNumber(phoneNumber),
        message_type: "button",
        message_text: msg.text || msg.template_name || "Message",
        status: msg.status,
        has_media: msg.has_media,
        created_at: msg.timestamp,
        message_id: msg.message_id || `${phoneNumber}-in-${idx}`,
      }));
    });

    replies.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setAllReplies(replies);
  }, [whatsappRepliesResponse]);

  const loadMessages = useCallback(
    async (offset = 0, status = DEFAULT_STATUS, limit = DEFAULT_LIMIT) => {
      if (!customerId || !campaign_id) return;
      try {
        const result = await fetchCampaignReport({
          customerId,
          campaign_id,
          page: 1,
          limit: 25,
          messages_offset: offset,
          messages_limit: limit,
          status,
          direction: "outbound",
          include_replies: false,
        }).unwrap();

        const campaignData = result?.data?.[0] || result?.data || {};
        const messagesData = campaignData?.messages || [];
        const summaryData = result?.summary || campaignData?.summary || null;

        setCampaign(campaignData);
        setMessages(messagesData);
        setSummary(summaryData);

        // If we got less than `limit` messages back, there's no next page
        setHasMore(messagesData.length >= limit);
      } catch (error) {
        console.error("API Error:", error);
        toast.error("Failed to fetch WhatsApp campaign details");
      } finally {
        setInitialLoading(false);
      }
    },
    [campaign_id, customerId, fetchCampaignReport],
  );

  // Initial load + reset whenever the campaign or the logged-in customer changes
  useEffect(() => {
    if (!customerId || !campaign_id) return;
    setInitialLoading(true);
    setCurrentOffset(0);
    setHasMore(true);
    setSummary(null);
    setPhoneSearch("");
    setStatusFilter(DEFAULT_STATUS);
    loadMessages(0, DEFAULT_STATUS, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign_id, customerId]);

  const handleNextPage = () => {
    const newOffset = currentOffset + rowsPerPage;
    setCurrentOffset(newOffset);
    loadMessages(newOffset, statusFilter, rowsPerPage);
  };

  const handlePrevPage = () => {
    const newOffset = Math.max(0, currentOffset - rowsPerPage);
    setCurrentOffset(newOffset);
    loadMessages(newOffset, statusFilter, rowsPerPage);
  };

  const handleStatusChipClick = (key) => {
    setStatusFilter(key);
    setCurrentOffset(0);
    loadMessages(0, key, rowsPerPage);
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = Number(event.target.value);
    setRowsPerPage(newLimit);
    setCurrentOffset(0);
    loadMessages(0, statusFilter, newLimit);
  };

  const handleTabChange = (event, newValue) => setActiveTab(newValue);

  // Current-page phone numbers — status filtering happens server-side now,
  // so this just shapes the current page's messages into rows.
  const phoneNumbers = useMemo(() => {
    if (!messages.length) return [];
    return messages
      .filter((msg) => msg.direction === "outbound" && msg.message_data?.type === "template")
      .map((msg) => ({
        phoneNumber: normalizePhoneNumber(msg.message_data?.to),
        status: msg.status,
        createdTime: msg.created_at,
        sentTime: msg.sent_at,
        deliveredTime: msg.delivered_at,
        readTime: msg.read_at,
        failedTime: msg.failed_at,
        messageId: msg.message_id,
        cost: msg.cost,
        deliveryTimeMs: msg.delivery_time_ms,
        readTimeMs: msg.read_time_ms,
        totalTimeMs: msg.total_time_ms,
        errorCode:
          msg.status_code ??
          msg.message_data?.error_code ??
          msg.message_data?.errors?.[0]?.code ??
          null,
        errorDescription:
          msg.description ||
          msg.message_data?.error_description ||
          msg.message_data?.errors?.[0]?.message ||
          msg.error_category ||
          null,
        errorCategory: msg.error_category,
        statusCode: msg.status_code,
      }));
  }, [messages]);

  // Chip counts reflect the whole campaign (from summary), not just the
  // currently loaded page — since the page itself is now status-filtered server-side.
  const statusCounts = {
    all: summary?.total_messages ?? 0,
    sent: summary?.by_status?.sent ?? 0,
    delivered: summary?.by_status?.delivered ?? 0,
    read: summary?.by_status?.read ?? 0,
    failed: summary?.failed_messages ?? summary?.by_status?.failed ?? 0,
    accepted: summary?.by_status?.accepted ?? 0,
  };

  const summaryChips = summary
    ? [
        { key: "total", label: "Total", value: summary.total_messages, color: "#0288d1" },
        { key: "successful", label: "Successful", value: summary.successful_messages, color: "#16a34a" },
        { key: "sent", label: "Sent", value: summary.by_status?.sent || 0, color: "#2563eb" },
        { key: "delivered", label: "Delivered", value: summary.by_status?.delivered || 0, color: "#16a34a" },
        { key: "read", label: "Read", value: summary.by_status?.read || 0, color: "#7c3aed" },
        { key: "failed", label: "Failed", value: summary.failed_messages, color: "#dc2626" },
      ]
    : [];

  // Only phone search is applied client-side now — status filtering happens
  // server-side via loadMessages(offset, statusFilter, limit)
  const filteredPhoneNumbers = useMemo(() => {
    let list = phoneNumbers;
    if (phoneSearch.trim()) {
      const s = phoneSearch.toLowerCase();
      list = list.filter((item) => item.phoneNumber?.toLowerCase().includes(s));
    }
    return list;
  }, [phoneNumbers, phoneSearch]);

  const filteredReplies = useMemo(() => {
    let list = allReplies;
    if (replySearch.trim()) {
      const s = replySearch.toLowerCase();
      list = list.filter(
        (item) =>
          item.phone_number?.toLowerCase().includes(s) ||
          item.message_text?.toLowerCase().includes(s) ||
          item.message_type?.toLowerCase().includes(s),
      );
    }
    return list;
  }, [allReplies, replySearch]);

  const paginatedReplies = useMemo(() => {
    const startIndex = (replyPage - 1) * replyRowsPerPage;
    const endIndex = startIndex + replyRowsPerPage;
    return filteredReplies.slice(startIndex, endIndex);
  }, [filteredReplies, replyPage, replyRowsPerPage]);

  useEffect(() => {
    setReplyPage(1);
  }, [replySearch]);

  const filterChips = [
    { key: "all", label: `All ` },
    { key: "sent", label: `Sent ` },
    { key: "delivered", label: `Delivered ` },
    { key: "read", label: `Read ` },
    { key: "failed", label: `Failed ` },
    { key: "accepted", label: "Awaited" },
  ];

  const currentPageNumber = Math.floor(currentOffset / rowsPerPage) + 1;

  // Handlers
  const handleClearFilters = () => {
    setPhoneSearch("");
    handleStatusChipClick(DEFAULT_STATUS);
  };

  const handleClearReplySearch = () => setReplySearch("");

  const handleReplyPageChange = (_, value) => setReplyPage(value);

  const handleReplyRowsPerPageChange = (event) => {
    const newLimit = Number(event.target.value);
    setReplyRowsPerPage(newLimit);
    setReplyPage(1);
  };

  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: "100%", margin: "0 auto" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="subtitle2" sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
            WhatsApp Campaign {campaign_id}
          </Typography>
        </Box>
      </Box>

      {/* Campaign Info */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1, backgroundColor: "rgba(255,255,255,0.7)", backdropFilter: "blur(10px)" }}>
        <Box display="flex" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">Campaign Name</Typography>
            <Typography variant="body2" fontWeight={500}>
              {campaign.campaign_name || whatsappRepliesResponse?.campaign_name || "Unnamed"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Total Numbers</Typography>
            <Typography variant="body2" fontWeight={500}>
              {campaign.total_no_of_phone_numbers || summary?.total_messages || "-"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Total Replies</Typography>
            <Typography variant="body2" fontWeight={500}>
              {allReplies.length}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Campaign Summary chips (from the server-side summary object) */}
      {summary && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1, borderRadius: 1, backgroundColor: "rgba(255,255,255,0.7)", backdropFilter: "blur(10px)" }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#0F172A", mb: 1 }}>
            Campaign Summary
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {summaryChips.map((chip) => (
              <Box
                key={chip.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "16px",
                  bgcolor: `${chip.color}15`,
                  border: `1px solid ${chip.color}30`,
                }}
              >
                <Typography sx={{ fontSize: "0.65rem", color: chip.color, fontWeight: 600 }}>
                  {chip.label}:
                </Typography>
                <Typography sx={{ fontSize: "0.7rem", color: chip.color, fontWeight: 700 }}>
                  {chip.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {/* Tabs */}
      <Paper variant="outlined" sx={{ mb: 1, borderRadius: 1, backgroundColor: "rgba(255,255,255,0.7)", backdropFilter: "blur(10px)" }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 40 }}>
          <Tab label="Phone Numbers" sx={{ minHeight: 40, fontSize: "0.75rem", fontWeight: 600 }} />
          <Tab
            label={
              <Badge color="error" badgeContent={allReplies.length} max={99}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ReplyIcon fontSize="small" />
                  Replies
                </Box>
              </Badge>
            }
            sx={{ minHeight: 40, fontSize: "0.75rem", fontWeight: 600 }}
          />
        </Tabs>
      </Paper>

      {/* Phone Numbers Tab */}
      {activeTab === 0 && (
        <>
          {/* Filter Section */}
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0.5, backgroundColor: "rgba(255,255,255,0.7)", backdropFilter: "blur(10px)", mb: 1 }}>
            <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
              {filterChips.map((chip) => (
                <Chip
                  key={chip.key}
                  label={chip.label}
                  size="small"
                  variant={statusFilter === chip.key ? "filled" : "outlined"}
                  color={
                    chip.key === "all" ? "default" :
                    chip.key === "sent" ? "info" :
                    chip.key === "delivered" ? "success" :
                    chip.key === "read" ? "primary" : "error"
                  }
                  onClick={() => handleStatusChipClick(chip.key)}
                  sx={{
                    fontSize: "0.6rem",
                    height: 20,
                    cursor: "pointer",
                    fontWeight: statusFilter === chip.key ? 700 : 400,
                    bgcolor: statusFilter === chip.key ? "#0288d1" : "transparent",
                    color: statusFilter === chip.key ? "white" : "#0288d1",
                    borderColor: "#0288d1",
                    "&:hover": {
                      bgcolor: statusFilter === chip.key ? "#0277bd" : "rgba(2,136,209,0.08)",
                    },
                  }}
                />
              ))}

              <TextField
                placeholder="Search number"
                value={phoneSearch}
                onChange={(e) => setPhoneSearch(e.target.value)}
                size="small"
                sx={{ ml: "auto", width: 180 }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 0.5 }} fontSize="small" />,
                  style: { fontSize: "0.75rem", height: 28 },
                }}
              />
              {(phoneSearch || statusFilter !== DEFAULT_STATUS) && (
                <IconButton size="small" onClick={handleClearFilters}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          </Paper>

          {/* Data Table */}
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ border: "1px solid #1b1b1b", borderRadius: 1, overflow: "hidden", backdropFilter: "blur(10px)", backgroundColor: "rgba(255,255,255,0.7)" }}
          >
            <Table size="small" stickyHeader>
              <TableHead sx={{ backgroundColor: "#0288d1" }}>
                <TableRow>
                  {["#", "Phone Number", "Status", "Created Time", "Sent Time", "Delivered Time", "Read Time", "Failed Time", "Delivery", "Error Code", "Error Description"].map((h, i) => (
                    <TableCell key={i} sx={{ color: "white", fontWeight: 600, fontSize: "0.7rem", py: 0.6, borderRight: "1px solid #1b1b1b" }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody sx={{ background: "transparent" }}>
                {messagesLoading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                ) : filteredPhoneNumbers.length > 0 ? (
                  filteredPhoneNumbers.map((item, idx) => (
                    <TableRow key={item.messageId || idx} hover>
                      <TableCell sx={cellSX}>{idx + 1 + currentOffset}</TableCell>
                      <TableCell sx={cellSX}>{maskPhone(item.phoneNumber)}</TableCell>
                      <TableCell sx={cellSX}>
                        <Chip
                          label={item.status?.toUpperCase() || "UNKNOWN"}
                          color={getStatusColor(item.status)}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.65rem", height: 18 }}
                        />
                      </TableCell>
                      <TableCell sx={cellSX}>{formatTimestamp(item.createdTime)}</TableCell>
                      <TableCell sx={cellSX}>{formatTimestamp(item.sentTime)}</TableCell>
                      <TableCell sx={cellSX}>{formatTimestamp(item.deliveredTime)}</TableCell>
                      <TableCell sx={cellSX}>{formatTimestamp(item.readTime)}</TableCell>
                      <TableCell sx={cellSX}>{formatTimestamp(item.failedTime)}</TableCell>
                      <TableCell sx={cellSX}>{formatTimeDuration(item.deliveryTimeMs)}</TableCell>
                      <TableCell sx={cellSX}>{item.errorCode ?? "-"}</TableCell>
                      <TableCell sx={{ ...cellSX, maxWidth: 220, whiteSpace: "normal", wordBreak: "break-word" }}>
                        {item.errorDescription ? (
                          <Tooltip title={`Code: ${item.statusCode || "-"} | Category: ${item.errorCategory || "-"}`}>
                            <span>{item.errorDescription}</span>
                          </Tooltip>
                        ) : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ fontSize: "0.75rem", py: 4, color: "#94A3B8" }}>
                      {phoneNumbers.length === 0 ? "No phone numbers found for this campaign." : "No numbers match your filters."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Offset-based pagination */}
          <Box display="flex" justifyContent="center" alignItems="center" gap={2} p={1.5} sx={{ borderTop: "1px solid #E2E8F0", bgcolor: "#F8FAFC" }}>
            <FormControl size="small" sx={{ minWidth: 80 }}>
              <Select value={rowsPerPage} onChange={handleRowsPerPageChange} sx={{ fontSize: "0.7rem", height: 28 }}>
                {[25, 50, 100].map((n) => (
                  <MenuItem key={n} value={n} sx={{ fontSize: "0.7rem" }}>{n}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              size="small"
              disabled={currentOffset === 0 || messagesLoading}
              onClick={handlePrevPage}
              sx={{
                fontSize: "0.72rem",
                textTransform: "none",
                bgcolor: currentOffset === 0 ? "#E2E8F0" : "#0288d1",
                color: currentOffset === 0 ? "#94A3B8" : "white",
                "&:hover": { bgcolor: currentOffset === 0 ? "#E2E8F0" : "#0277bd" },
                minWidth: 100,
              }}
            >
              Previous
            </Button>

            <Box sx={{ px: 2, py: 0.5, bgcolor: "white", borderRadius: "6px", border: "1px solid #E2E8F0", minWidth: 120, textAlign: "center" }}>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#334155" }}>
                Page {currentPageNumber}
              </Typography>
              <Typography sx={{ fontSize: "0.65rem", color: "#64748B" }}>
                Offset: {currentOffset}
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="small"
              disabled={!hasMore || messagesLoading}
              onClick={handleNextPage}
              sx={{
                fontSize: "0.72rem",
                textTransform: "none",
                bgcolor: !hasMore ? "#E2E8F0" : "#0288d1",
                color: !hasMore ? "#94A3B8" : "white",
                "&:hover": { bgcolor: !hasMore ? "#E2E8F0" : "#0277bd" },
                minWidth: 100,
              }}
            >
              Next
            </Button>
          </Box>
        </>
      )}

      {/* Replies Tab (button replies only) */}
      {activeTab === 1 && (
        <>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 0.5, backgroundColor: "rgba(255,255,255,0.7)", backdropFilter: "blur(10px)", mb: 1 }}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
              {allReplies.length > 0 && (
                <Chip
                  label={`Button: ${allReplies.length}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ fontSize: "0.6rem", height: 20 }}
                />
              )}

              <TextField
                placeholder="Search replies..."
                value={replySearch}
                onChange={(e) => setReplySearch(e.target.value)}
                size="small"
                sx={{ ml: "auto", width: 200 }}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 0.5 }} fontSize="small" />,
                  endAdornment: replySearch && (
                    <IconButton size="small" onClick={handleClearReplySearch}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ),
                  style: { fontSize: "0.75rem", height: 28 },
                }}
              />
              <Typography sx={{ fontSize: "0.7rem", color: "#64748B" }}>
                Total: {filteredReplies.length} replies
              </Typography>
            </Box>
          </Paper>

          <Paper
            variant="outlined"
            sx={{ borderRadius: 1, overflow: "hidden", border: "1px solid #1b1b1b", backdropFilter: "blur(10px)", backgroundColor: "rgba(255,255,255,0.7)" }}
          >
            {repliesLoading ? (
              <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
                <CircularProgress size={22} />
              </Box>
            ) : paginatedReplies.length > 0 ? (
              <>
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead sx={{ backgroundColor: "#0288d1" }}>
                      <TableRow>
                        {["#", "Phone Number", "Message", "Type", "Status", "Received Time"].map((h, i) => (
                          <TableCell key={i} sx={{ color: "white", fontWeight: 600, fontSize: "0.7rem", py: 0.6, borderRight: "1px solid #1b1b1b" }}>
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody sx={{ background: "transparent" }}>
                      {paginatedReplies.map((reply, idx) => (
                        <TableRow
                          key={reply.message_id || idx}
                          hover
                          sx={{ backgroundColor: "#f0f9ff", "&:hover": { backgroundColor: "#e3f2fd" } }}
                        >
                          <TableCell sx={cellSX}>{idx + 1 + (replyPage - 1) * replyRowsPerPage}</TableCell>
                          <TableCell sx={cellSX}>{maskPhone(reply.phone_number)}</TableCell>
                          <TableCell sx={{ ...cellSX, maxWidth: 300 }}>
                            <Tooltip title={reply.message_text || ""}>
                              <Typography
                                variant="body2"
                                sx={{ fontSize: "0.7rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 280 }}
                              >
                                {reply.message_text || "-"}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={cellSX}>
                            <Chip
                              label={reply.message_type?.toUpperCase() || "UNKNOWN"}
                              color="primary"
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.6rem", height: 18 }}
                            />
                          </TableCell>
                          <TableCell sx={cellSX}>
                            <Chip
                              label={reply.status?.toUpperCase() || "UNKNOWN"}
                              color={getStatusColor(reply.status)}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.6rem", height: 18 }}
                            />
                          </TableCell>
                          <TableCell sx={cellSX}>{formatTimestamp(reply.created_at)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredReplies.length > 0 && (
                  <Box display="flex" justifyContent="space-between" alignItems="center" p={1} borderTop="1px solid #E2E8F0">
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography sx={{ fontSize: "0.72rem", color: "#64748B" }}>
                        Showing {paginatedReplies.length} of {filteredReplies.length}
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 70 }}>
                        <Select value={replyRowsPerPage} onChange={handleReplyRowsPerPageChange} sx={{ fontSize: "0.72rem", height: 28 }}>
                          {[10, 25, 50, 100].map((n) => (
                            <MenuItem key={n} value={n} sx={{ fontSize: "0.72rem" }}>{n}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Pagination
                      count={Math.ceil(filteredReplies.length / replyRowsPerPage)}
                      page={replyPage}
                      onChange={handleReplyPageChange}
                      size="small"
                      color="primary"
                    />
                  </Box>
                )}
              </>
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography variant="caption" color="text.secondary">
                  {allReplies.length === 0 ? "No button replies found for this campaign." : "No replies match your search criteria."}
                </Typography>
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default WhatsappDetailPage;